'use client';

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Search } from 'lucide-react';
import type { KanbanStylePreset } from './KanbanStylePresets';

import {
  SCHEDULE_SIDEBAR_PRIORITY_OPTIONS,
  SCHEDULE_SIDEBAR_SORT_OPTIONS,
  SCHEDULE_SIDEBAR_STATUS_OPTIONS,
  type ScheduleSidebarContactOption,
  type ScheduleSidebarSortOrder,
} from './scheduleFilters';

export const SCHEDULE_SIDEBAR_WIDTH_CLASS = 'w-72';

interface ScheduleSidebarShellProps {
  title: string;
  count: number;
  icon: LucideIcon;
  isDark: boolean;
  searchPlaceholder: string;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  priorityFilter: string;
  onPriorityFilterChange: (value: string) => void;
  contactFilter: string;
  onContactFilterChange: (value: string) => void;
  contactOptions: ScheduleSidebarContactOption[];
  contactLabel?: string;
  sortOrder: ScheduleSidebarSortOrder;
  onSortOrderChange: (value: ScheduleSidebarSortOrder) => void;
  presetConfig?: KanbanStylePreset;
  containerClassName?: string;
  scrollBodyClassName?: string;
  scrollBodyRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}

export default function ScheduleSidebarShell({
  title,
  count,
  icon: Icon,
  isDark,
  searchPlaceholder,
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  contactFilter,
  onContactFilterChange,
  contactOptions,
  contactLabel = 'All Contacts',
  sortOrder,
  onSortOrderChange,
  presetConfig,
  containerClassName,
  scrollBodyClassName,
  scrollBodyRef,
  children,
}: ScheduleSidebarShellProps) {
  const viewStyles = presetConfig?.views;
  const countBadgeClasses = presetConfig?.column?.countBadge
    ? `${presetConfig.column.countBadge.background} ${presetConfig.column.countBadge.textColor} ${presetConfig.column.countBadge.borderRadius}`
    : (isDark ? 'bg-gray-700 text-gray-400 rounded-full' : 'bg-gray-200 text-gray-500 rounded-full');
  const headerClasses = `${viewStyles?.headerBg || ''} ${viewStyles?.dividerBorder || (isDark ? 'border-gray-700' : 'border-gray-200')}`;
  const headingTextClass = viewStyles?.headingText || (isDark ? 'text-gray-200' : 'text-gray-800');
  const bodyTextClass = viewStyles?.bodyText || (isDark ? 'text-gray-200' : 'text-gray-900');
  const mutedTextClass = viewStyles?.mutedText || (isDark ? 'text-gray-500' : 'text-gray-400');
  const inputClasses = `${viewStyles?.inputBg || (isDark ? 'bg-gray-800' : 'bg-white')} ${viewStyles?.inputBorder || (isDark ? 'border-gray-700' : 'border-gray-300')} ${bodyTextClass}`;

  return (
    <>
      <div className={`flex-shrink-0 border-b p-3 ${headerClasses}`}>
        <h3 className={`mb-2 flex items-center justify-between text-sm font-semibold ${headingTextClass}`}>
          <span className="flex items-center gap-1.5">
            <Icon className="h-4 w-4" />
            {title}
          </span>
          <span className={`px-1.5 py-0.5 text-xs font-normal ${countBadgeClasses}`}>
            {count}
          </span>
        </h3>

        <div className="relative mb-2">
          <Search className={`absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedTextClass}`} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            className={`w-full rounded border py-1.5 pl-8 pr-3 text-sm placeholder-gray-400 dark:placeholder-gray-500 ${inputClasses}`}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={statusFilter}
            onChange={(event) => onStatusFilterChange(event.target.value)}
            className={`w-full rounded border px-2 py-1.5 text-sm ${inputClasses}`}
          >
            {SCHEDULE_SIDEBAR_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(event) => onPriorityFilterChange(event.target.value)}
            className={`w-full rounded border px-2 py-1.5 text-sm ${inputClasses}`}
          >
            {SCHEDULE_SIDEBAR_PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={contactFilter}
            onChange={(event) => onContactFilterChange(event.target.value)}
            className={`w-full rounded border px-2 py-1.5 text-sm ${inputClasses}`}
          >
            <option value="all">{contactLabel}</option>
            {contactOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={sortOrder}
            onChange={(event) => onSortOrderChange(event.target.value as ScheduleSidebarSortOrder)}
            className={`w-full rounded border px-2 py-1.5 text-sm ${inputClasses}`}
          >
            {SCHEDULE_SIDEBAR_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div ref={scrollBodyRef} className={scrollBodyClassName || 'flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 space-y-2'}>
        {children}
      </div>
    </>
  );
}
